import { useStore } from "zustand";
import { createStore } from "zustand/vanilla";

import type { Priority } from "@/components/ui/PriorityIcon/priority";

/**
 * Global UI state that is not board data and not in the URL: which dialog is
 * open, whether the mobile sidebar is showing. Board data lives in the sync
 * replica, and anything worth bookmarking — the open task — lives in the URL.
 */

export interface NewTaskDefaults {
  projectId?: string | undefined;
  stateId?: string | undefined;
  /** null opens the dialog explicitly unassigned, e.g. from a "No assignee" filter. */
  assigneeId?: string | null | undefined;
  priority?: Priority | undefined;
  labelIds?: readonly string[] | undefined;
}

/**
 * What the command menu is showing: everything, or one list for one task
 * (opened with S, A, P or L on a card, the way Linear does it).
 */
export type PaletteMode =
  { kind: "root" } | { kind: "status" | "assignee" | "priority" | "labels"; taskId: string };

interface UiState {
  newTask: NewTaskDefaults | null;
  newProject: boolean;
  sidebarOpen: boolean;
  palette: PaletteMode | null;
  shortcutsOpen: boolean;
}

const uiStore = createStore<UiState>(() => ({
  newTask: null,
  newProject: false,
  sidebarOpen: false,
  palette: null,
  shortcutsOpen: false,
}));

export const ui = {
  openNewTask: (defaults: NewTaskDefaults = {}) => {
    uiStore.setState({ newTask: defaults });
  },
  closeNewTask: () => {
    uiStore.setState({ newTask: null });
  },
  openNewProject: () => {
    uiStore.setState({ newProject: true });
  },
  closeNewProject: () => {
    uiStore.setState({ newProject: false });
  },
  setSidebarOpen: (open: boolean) => {
    uiStore.setState({ sidebarOpen: open });
  },
  openPalette: (mode: PaletteMode = { kind: "root" }) => {
    uiStore.setState({ palette: mode });
  },
  closePalette: () => {
    uiStore.setState({ palette: null });
  },
  /** Ctrl+K: open the menu, or close it if it is already open. */
  togglePalette: () => {
    uiStore.setState((state) => ({ palette: state.palette ? null : { kind: "root" } }));
  },
  openShortcuts: () => {
    uiStore.setState({ shortcutsOpen: true });
  },
  closeShortcuts: () => {
    uiStore.setState({ shortcutsOpen: false });
  },
};

export function useUi<T>(selector: (state: UiState) => T): T {
  return useStore(uiStore, selector);
}
